import {
  CognitoIdToken,
  CognitoAccessToken,
  CognitoRefreshToken,
  CognitoUserSession,
} from 'amazon-cognito-identity-js';
import { mockAuthService } from './auth.mock';

// Fallback to environment variables, but prefer dynamic config
const envClientId = import.meta.env.VITE_COGNITO_CLIENT_ID;
const envCognitoDomain = import.meta.env.VITE_COGNITO_DOMAIN;
const redirectUri = `${window.location.origin}/auth/callback`;
const useMockAuth = import.meta.env.VITE_USE_MOCK_AUTH === 'true';

// Token storage key
const TOKEN_STORAGE_KEY = 'cognitoTokens';

// Auth config cache key
const AUTH_CONFIG_KEY = 'authConfig';

// Logout flag to prevent auto-login after intentional logout
const LOGOUT_FLAG_KEY = 'intentionalLogout';

// Helper to get stored tokens
function getStoredTokens(): { idToken: string; accessToken: string; refreshToken: string; expiresAt: number } | null {
  const stored = localStorage.getItem(TOKEN_STORAGE_KEY);
  if (!stored) return null;
  try {
    return JSON.parse(stored);
  } catch {
    return null;
  }
}

// Helper to decode JWT payload
function decodeJwtPayload(token: string): any {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    return JSON.parse(jsonPayload);
  } catch {
    return null;
  }
}

// Auth config type
interface AuthConfig {
  sso_configured: boolean;
  sso_provider: string | null;
  cognito_domain: string;
  client_id: string;
  idp_name: string | null;
  admin_login_available: boolean;
  cached_at?: number;
}

// Cache TTL for auth config (5 minutes)
const AUTH_CONFIG_CACHE_TTL = 5 * 60 * 1000;

// Get cached auth config or fetch from API
async function getAuthConfig(): Promise<AuthConfig> {
  const cached = localStorage.getItem(AUTH_CONFIG_KEY);
  if (cached) {
    try {
      const config = JSON.parse(cached) as AuthConfig;
      if (config.cached_at && Date.now() - config.cached_at < AUTH_CONFIG_CACHE_TTL) {
        return config;
      }
    } catch {
      // Invalid cache, fetch fresh
    }
  }

  try {
    const response = await fetch(`${import.meta.env.VITE_API_ENDPOINT || '/api'}/auth/config`);
    if (response.ok) {
      const config = await response.json();
      config.cached_at = Date.now();
      localStorage.setItem(AUTH_CONFIG_KEY, JSON.stringify(config));
      return config;
    }
  } catch (error) {
    console.error('Failed to fetch auth config:', error);
  }

  return {
    sso_configured: true,
    sso_provider: null,
    cognito_domain: envCognitoDomain || '',
    client_id: envClientId || '',
    idp_name: null,
    admin_login_available: true,
  };
}

function clearAuthConfigCache(): void {
  localStorage.removeItem(AUTH_CONFIG_KEY);
}

// Token refresh deduplication (module-level)
let isRefreshing = false;
let refreshPromise: Promise<CognitoUserSession | null> | null = null;
const REFRESH_BUFFER_MS = 60 * 1000; // Refresh 60s before actual expiry

const baseAuthService = useMockAuth ? mockAuthService : {
  async getLoginUrl(): Promise<string> {
    const config = await getAuthConfig();
    const clientId = config.client_id || envClientId;
    const cognitoDomain = config.cognito_domain || envCognitoDomain;

    const params = new URLSearchParams({
      client_id: clientId,
      response_type: 'code',
      scope: 'email openid profile',
      redirect_uri: redirectUri,
    });

    if (config.idp_name) {
      params.set('identity_provider', config.idp_name);
    }

    return `https://${cognitoDomain}/oauth2/authorize?${params.toString()}`;
  },

  async exchangeCodeForTokens(code: string): Promise<boolean> {
    try {
      const config = await getAuthConfig();
      const clientId = config.client_id || envClientId;
      const cognitoDomain = config.cognito_domain || envCognitoDomain;
      const tokenEndpoint = `https://${cognitoDomain}/oauth2/token`;

      const params = new URLSearchParams({
        grant_type: 'authorization_code',
        client_id: clientId,
        code: code,
        redirect_uri: redirectUri,
      });

      const response = await fetch(tokenEndpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: params.toString(),
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Token exchange failed:', errorText);
        return false;
      }

      const tokens = await response.json();
      localStorage.setItem(TOKEN_STORAGE_KEY, JSON.stringify({
        idToken: tokens.id_token,
        accessToken: tokens.access_token,
        refreshToken: tokens.refresh_token,
        expiresAt: Date.now() + (tokens.expires_in * 1000),
      }));
      return true;
    } catch (error) {
      console.error('Error exchanging code for tokens:', error);
      return false;
    }
  },

  async getLogoutUrl(): Promise<string> {
    const config = await getAuthConfig();
    const clientId = config.client_id || envClientId;
    const cognitoDomain = config.cognito_domain || envCognitoDomain;
    const params = new URLSearchParams({
      client_id: clientId,
      logout_uri: `${window.location.origin}/logout`,
    });
    return `https://${cognitoDomain}/logout?${params.toString()}`;
  },

  async login(): Promise<void> {
    window.location.href = await this.getLoginUrl();
  },

  async revokeRefreshToken(): Promise<void> {
    const tokens = getStoredTokens();
    if (!tokens?.refreshToken) return;
    try {
      const config = await getAuthConfig();
      const clientId = config.client_id || envClientId;
      const cognitoDomain = config.cognito_domain || envCognitoDomain;
      await fetch(`https://${cognitoDomain}/oauth2/revoke`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams({
          token: tokens.refreshToken,
          client_id: clientId,
        }).toString(),
      });
    } catch {
      // Best-effort revocation; don't block logout
    }
  },

  async logout(): Promise<void> {
    await this.revokeRefreshToken();
    localStorage.removeItem(TOKEN_STORAGE_KEY);
    sessionStorage.setItem(LOGOUT_FLAG_KEY, 'true');
    window.location.href = '/logout';
  },

  hasIntentionallyLoggedOut(): boolean {
    return sessionStorage.getItem(LOGOUT_FLAG_KEY) === 'true';
  },

  clearLogoutFlag(): void {
    sessionStorage.removeItem(LOGOUT_FLAG_KEY);
  },

  clearAuthConfigCache,

  getTokenExpiryTime(): number | null {
    const tokens = getStoredTokens();
    if (!tokens) return null;
    return tokens.expiresAt;
  },

  getCurrentSession(): Promise<CognitoUserSession | null> {
    return new Promise((resolve) => {
      const tokens = getStoredTokens();
      if (!tokens) { resolve(null); return; }
      if (tokens.expiresAt < Date.now()) {
        localStorage.removeItem(TOKEN_STORAGE_KEY);
        resolve(null);
        return;
      }
      try {
        const idToken = new CognitoIdToken({ IdToken: tokens.idToken });
        const accessToken = new CognitoAccessToken({ AccessToken: tokens.accessToken });
        const refreshToken = new CognitoRefreshToken({ RefreshToken: tokens.refreshToken });
        resolve(new CognitoUserSession({ IdToken: idToken, AccessToken: accessToken, RefreshToken: refreshToken }));
      } catch (error) {
        console.error('Error creating session from stored tokens:', error);
        resolve(null);
      }
    });
  },

  getCurrentUser(): Promise<any> {
    return new Promise((resolve) => {
      const tokens = getStoredTokens();
      if (!tokens) { resolve(null); return; }
      if (tokens.expiresAt < Date.now()) {
        localStorage.removeItem(TOKEN_STORAGE_KEY);
        resolve(null);
        return;
      }
      const payload = decodeJwtPayload(tokens.idToken);
      if (!payload) { resolve(null); return; }
      resolve({
        username: payload['cognito:username'] || payload['sub'] || payload['email'],
        attributes: {
          email: payload['email'],
          email_verified: payload['email_verified'],
          given_name: payload['given_name'],
          family_name: payload['family_name'],
          'custom:groups': payload['custom:groups'],
        },
      });
    });
  },

  async getIdToken(): Promise<string | null> {
    const tokens = getStoredTokens();
    if (!tokens) return null;

    // Token expired or about to expire — try transparent refresh
    if (tokens.expiresAt < Date.now() + REFRESH_BUFFER_MS) {
      if (!isRefreshing) {
        isRefreshing = true;
        refreshPromise = this.refreshSession().finally(() => {
          isRefreshing = false;
          refreshPromise = null;
        });
      }
      const session = await (refreshPromise ?? Promise.resolve(null));
      if (!session) {
        localStorage.removeItem(TOKEN_STORAGE_KEY);
        return null;
      }
      const refreshedTokens = getStoredTokens();
      return refreshedTokens?.idToken || null;
    }

    return tokens.idToken;
  },

  async isAuthenticated(): Promise<boolean> {
    const tokens = getStoredTokens();
    if (!tokens) return false;
    if (tokens.expiresAt < Date.now()) {
      localStorage.removeItem(TOKEN_STORAGE_KEY);
      return false;
    }
    return true;
  },

  async refreshSession(): Promise<CognitoUserSession | null> {
    const tokens = getStoredTokens();
    if (!tokens || !tokens.refreshToken) return null;

    try {
      const config = await getAuthConfig();
      const clientId = config.client_id || envClientId;
      const cognitoDomain = config.cognito_domain || envCognitoDomain;
      const tokenEndpoint = `https://${cognitoDomain}/oauth2/token`;

      const params = new URLSearchParams({
        grant_type: 'refresh_token',
        client_id: clientId,
        refresh_token: tokens.refreshToken,
      });

      const response = await fetch(tokenEndpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: params.toString(),
      });

      if (!response.ok) {
        console.error('Token refresh failed');
        localStorage.removeItem(TOKEN_STORAGE_KEY);
        return null;
      }

      const newTokens = await response.json();
      localStorage.setItem(TOKEN_STORAGE_KEY, JSON.stringify({
        idToken: newTokens.id_token,
        accessToken: newTokens.access_token,
        refreshToken: tokens.refreshToken,
        expiresAt: Date.now() + (newTokens.expires_in * 1000),
      }));

      const idToken = new CognitoIdToken({ IdToken: newTokens.id_token });
      const accessToken = new CognitoAccessToken({ AccessToken: newTokens.access_token });
      const refreshToken = new CognitoRefreshToken({ RefreshToken: tokens.refreshToken });
      return new CognitoUserSession({ IdToken: idToken, AccessToken: accessToken, RefreshToken: refreshToken });
    } catch (error) {
      console.error('Error refreshing session:', error);
      localStorage.removeItem(TOKEN_STORAGE_KEY);
      return null;
    }
  },
};

export const authService = baseAuthService;
